import { theme } from '@ovotech/element-v5';
import React from 'react';
import tick2 from '@/src/resources/chargeAnytime/tick2.svg';
import {
  Features,
  PlanDetails,
  useChargeAnytimeContext,
} from '@monovo/ev-anytime';
import { IconMapping } from './icon-mapping';
import {
  BannerWrapper,
  Banner,
  FeatureIcon,
  BadgeContainerLarge,
  BadgeContainerSmall,
  Body,
  StyledHeading3,
  PriceGroup,
  PriceInfoText,
  FeaturesWrapper,
  FeaturesHeading,
  FeaturesList,
  FeatureItem,
  TickImage,
  FullDetailsWrapper,
  ContentWrapper,
  WhiteCard,
  StyledPlanCard,
  PlanBodyHeading,
  PriceHeading,
  StyledBadge,
  SelectPlanButton,
  StyledOutlineButton,
  StyledDisplay4,
} from './ChargeAnytimePlans.styled';

export type Variant = 'selected-plan' | 'choose-plan' | 'plan-details';

type Props = {
  plan: PlanDetails;
  ctaText?: string;
  fullDetails?: React.ReactNode[];
  onCtaClick?: (planId: string) => void;
  showMultipleEvsBadge?: boolean;
  variant: Variant;
  'data-testid'?: string;
  /**
   * Renders the CTA as a solid dark button instead of the outline default.
   * Used by the Charge Anytime signup journey; the switch journey keeps the
   * outline button.
   */
  useSolidCta?: boolean;
  /**
   * Omits the `multipleEvs` row from the features list. The signup journey
   * surfaces multi-EV support via the card badge instead.
   */
  hideMultipleEvsFeature?: boolean;
};

// Sub-component for the top banner and conditional badges
const PlanBanner = ({
  plan,
  showMultipleEvsBadge,
  variant,
}: Pick<Props, 'plan' | 'showMultipleEvsBadge' | 'variant'>) => {
  const { partnership } = useChargeAnytimeContext();
  const getFeatureIcon = () => {
    switch (plan.id) {
      case 'payg':
        return {
          icon: IconMapping.payg,
          backgroundColor: theme.color.brand.brand,
          hasPartnershipImage: false,
        };
      case 'payg-volvo':
        return {
          icon: IconMapping['payg-volvo'],
          backgroundColor: theme.color.brand.brand,
          hasPartnershipImage: false,
        };
      case 'paym-tier-one':
        return {
          icon: IconMapping['paym-tier-one'],
          backgroundColor: theme.color.brand.bright,
          hasPartnershipImage: false,
        };
      case 'paym-tier-two':
        return {
          icon: IconMapping['paym-tier-two'],
          backgroundColor: theme.color.brand.bright,
          hasPartnershipImage: false,
        };
      case 'paym-multi-tier-one':
        return {
          icon: IconMapping['paym-multi-tier-one'],
          backgroundColor: theme.color.brand.dim,
          hasPartnershipImage: false,
        };
      case 'paym-multi-tier-two':
        return {
          icon: IconMapping['paym-multi-tier-two'],
          backgroundColor: theme.color.brand.dim,
          hasPartnershipImage: false,
        };
      case 'paym-oem-tier-one':
        if (partnership?.toUpperCase() === 'HYUNDAI') {
          return {
            icon: IconMapping.hyundai,
            backgroundColor: theme.color.brand.brand,
            hasPartnershipImage: true,
          };
        }
        if (partnership?.toUpperCase() === 'KIA') {
          return {
            icon: IconMapping.kia,
            backgroundColor: theme.color.brand.brand,
            hasPartnershipImage: true,
          };
        }
        return {
          icon: IconMapping['paym-oem-tier-one'],
          backgroundColor: theme.color.brand.bright,
          hasPartnershipImage: false,
        };
      default:
        return {
          icon: IconMapping['paym-tier-one'],
          backgroundColor: theme.color.brand.bright,
          hasPartnershipImage: false,
        };
    }
  };

  const iconDetails = getFeatureIcon();

  return (
    <BannerWrapper>
      <Banner
        backgroundColor={iconDetails.backgroundColor}
        hasPartnershipImage={iconDetails.hasPartnershipImage}
      >
        <FeatureIcon
          src={iconDetails.icon}
          alt={`${plan.miles} plan image`}
          hasPartnershipImage={iconDetails.hasPartnershipImage}
        />
      </Banner>
      {showMultipleEvsBadge && variant === 'choose-plan' && (
        <BadgeContainerSmall>
          <StyledBadge>Multiple EVs</StyledBadge>
        </BadgeContainerSmall>
      )}
      {showMultipleEvsBadge && variant === 'selected-plan' && (
        <BadgeContainerLarge>
          <StyledBadge>Multiple EVs</StyledBadge>
        </BadgeContainerLarge>
      )}
    </BannerWrapper>
  );
};

// Sub-component for the main body content (title, price, CTA)
const PlanBody = ({
  plan,
  ctaText,
  onCtaClick,
  'data-testid': dataTestId,
  variant,
  useSolidCta,
}: Pick<
  Props,
  'plan' | 'ctaText' | 'onCtaClick' | 'data-testid' | 'variant' | 'useSolidCta'
>) => {
  const planId = plan.id;
  const { showKwhVariant } = useChargeAnytimeContext();

  const isPaygPlan = plan.productType === 'payg' || plan.id === 'payg-volvo';
  const shouldUseVariant = isPaygPlan && showKwhVariant;

  const priceToDisplay =
    shouldUseVariant && plan.pricePerKwh ? plan.pricePerKwh : plan.price;

  const subtextToDisplay =
    shouldUseVariant && plan.priceInfoVariant
      ? plan.priceInfoVariant
      : plan.priceInfo;

  const mainInfoToDisplay = plan.miles;

  return (
    <Body>
      <div>
        <PlanBodyHeading>{plan.title}</PlanBodyHeading>
        {!isPaygPlan && <PriceHeading>Up to</PriceHeading>}
        <StyledHeading3>
          {mainInfoToDisplay}
          {!isPaygPlan && variant !== 'selected-plan' && planId !== 'payg-volvo'
            ? '¹'
            : ''}
        </StyledHeading3>
      </div>
      <PriceGroup>
        <StyledDisplay4>{priceToDisplay}</StyledDisplay4>
        <PriceInfoText>
          {subtextToDisplay}
          {!shouldUseVariant &&
          isPaygPlan &&
          variant !== 'selected-plan' &&
          planId !== 'payg-volvo'
            ? '²'
            : ''}
        </PriceInfoText>
      </PriceGroup>
      {ctaText && useSolidCta ? (
        <SelectPlanButton
          onClick={() => onCtaClick?.(plan.id)}
          data-testid={`${dataTestId}-cta`}
        >
          {ctaText}
        </SelectPlanButton>
      ) : ctaText ? (
        <StyledOutlineButton
          variant="outline"
          fullWidth={true}
          onClick={() => onCtaClick?.(plan.id)}
          data-testid={`${dataTestId}-cta`}
        >
          {ctaText}
        </StyledOutlineButton>
      ) : null}
    </Body>
  );
};

// Sub-component for the features list
const PlanFeatures = ({
  features,
  hideMultipleEvsFeature,
}: {
  features: Features;
  hideMultipleEvsFeature?: boolean;
}) => {
  const filteredFeatures = Object.entries(features).filter(
    ([featureKey]) => !(hideMultipleEvsFeature && featureKey === 'multipleEvs'),
  );

  return (
    <FeaturesWrapper>
      <FeaturesHeading>Features included:</FeaturesHeading>
      <FeaturesList>
        {filteredFeatures.map(([featureKey, feature], index) => (
          <FeatureItem key={featureKey} data-testid={`feature-${index}`}>
            <TickImage src={tick2} alt="tick" />
            <span>
              {feature}
              {featureKey === 'homeChargingMiles' ? '¹' : ''}
            </span>
          </FeatureItem>
        ))}
      </FeaturesList>
    </FeaturesWrapper>
  );
};

const ChargeAnytimePlans: React.FC<Props> = props => {
  const {
    plan,
    fullDetails,
    variant,
    'data-testid': dataTestId,
    hideMultipleEvsFeature,
  } = props;

  return (
    <StyledPlanCard data-testid={dataTestId}>
      <ContentWrapper>
        <WhiteCard>
          <PlanBanner {...props} />
          <PlanBody {...props} />
        </WhiteCard>

        {plan.features && variant !== 'selected-plan' && (
          <PlanFeatures
            features={plan.features}
            hideMultipleEvsFeature={hideMultipleEvsFeature}
          />
        )}
        {fullDetails && <FullDetailsWrapper>{fullDetails}</FullDetailsWrapper>}
      </ContentWrapper>
    </StyledPlanCard>
  );
};

export default ChargeAnytimePlans;
